// Ids, versions, and the one rule that makes this form safe to hand to anyone: the user never
// types the version.
//
// `score_spec_id` must end in `_v<N>` and `version` must equal that `<N>`. Create enforces the
// FORMER and not the LATTER — the two are only compared at lock, which is irreversible and has no
// undo. So both come off one number here, and the number comes off whether the stem changed.

import { SPEC_ID_REGEX, FEED_NAME_SEPARATOR } from '@features/scoreSpec/specSchema';

// The stem alone — what the user edits. The `_v<N>` is appended for them.
export const STEM_REGEX = /^[a-z][a-z0-9_]*$/;

const VERSION_SUFFIX = /_v(\d+)$/i;

// 'outlook_visibility_ect_v3' -> { stem: 'outlook_visibility_ect', version: 3 }. An id that does
// not end in _vN keeps its whole self as the stem and falls back to the record's own version.
export const splitSpecId = (specId, fallbackVersion) => {
  const id = String(specId || '');
  const match = VERSION_SUFFIX.exec(id);
  if (!match) {
    return { stem: id, version: Number(fallbackVersion) || 1 };
  }
  return { stem: id.slice(0, match.index), version: Number(match[1]) };
};

export const fullSpecId = (stem, version) => `${stem}_v${version}`;

// Two intents, one control. Keeping the stem is a SUCCESSOR — the next version of the same
// question, so the number carries on from the source. Typing a new stem is a FORK — a different
// question reusing the source's machinery, and a different question's first version is 1.
export const derivedVersion = (stem, source) => (stem === source.stem ? source.version + 1 : 1);

export const isFork = (stem, source) => stem !== source.stem;

// The pattern the API applies to the assembled id, not to the stem. Checked separately so a stem
// the form thought was fine can still be caught before it becomes a 422.
//
// `__` is a second, separate rule: the pattern allows `_` and therefore allows `__`, but the
// published feed is `score_spec_<id>[__<name>]`, so a doubled underscore inside the id makes that
// name ambiguous to resolve back to a spec and score.
export const isValidSpecId = (specId) => {
  const id = String(specId || '');
  return SPEC_ID_REGEX.test(id) && !FEED_NAME_SEPARATOR.test(id);
};
